import React, { useState, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { useSettings } from '../context/SettingsContext';
import './SessionReportModal.css';

// ── Patient Report Template ──

function generatePatientReport(session) {
  const lines = [];
  lines.push('ZUSAMMENFASSUNG IHRES ARZTBESUCHES');
  lines.push('=====================================');
  lines.push(`Datum: ${formatDateStr()}`);
  lines.push('');

  if (session.summary && Array.isArray(session.summary) && session.summary.length > 0) {
    lines.push('WAS WIR HEUTE BESPROCHEN HABEN');
    lines.push('--------------------------------');
    session.summary.forEach((section) => {
      (section.items || []).forEach((item) => {
        lines.push(`• ${item}`);
      });
    });
    lines.push('');
  }

  if (session.manualNotes) {
    lines.push('HINWEISE IHRES ARZTES');
    lines.push('----------------------');
    lines.push(session.manualNotes);
    lines.push('');
  }

  const { primary, secondary } = categorizeDiagnoses(session);
  const hasDiagnoses = session.diagnoses && session.diagnoses.length > 0;

  if (hasDiagnoses) {
    const selectedDiagnoses = primary.length > 0 ? [...primary, ...secondary] : [];
    const displayDiagnoses = selectedDiagnoses.length > 0 ? selectedDiagnoses : session.diagnoses.slice(0, 2);

    if (selectedDiagnoses.length > 0) {
      lines.push('IHRE DIAGNOSE(N)');
      lines.push('-----------------');
    } else {
      lines.push('MÖGLICHE DIAGNOSE(N)');
      lines.push('--------------------');
      lines.push('(Ihr Arzt hat folgende Diagnosen als möglich eingestuft. Die abschließende Diagnose erfordert ggf. weitere Untersuchungen.)');
    }

    displayDiagnoses.forEach((d) => {
      lines.push(`• ${d.name}`);
      if (d.reason) lines.push(`  ${d.reason}`);
    });
    lines.push('');

    const relevantForDetails = selectedDiagnoses.length > 0 ? selectedDiagnoses : session.diagnoses;
    const allMeds = [...new Set(relevantForDetails.flatMap((d) => d.medications || []))];
    const allTreatments = [...new Set(relevantForDetails.flatMap((d) => d.treatment || []))];
    const allDiagnostics = [...new Set(relevantForDetails.flatMap((d) => d.diagnostics || []))];

    if (allMeds.length > 0) {
      lines.push('IHRE MEDIKAMENTE');
      lines.push('-----------------');
      allMeds.forEach((m) => lines.push(`• ${m}`));
      lines.push('');
    }

    if (allTreatments.length > 0) {
      lines.push('EMPFOHLENE MAßNAHMEN');
      lines.push('----------------------');
      allTreatments.forEach((t) => lines.push(`• ${t}`));
      lines.push('');
    }

    if (allDiagnostics.length > 0) {
      lines.push('GEPLANTE UNTERSUCHUNGEN');
      lines.push('------------------------');
      allDiagnostics.forEach((d) => lines.push(`• ${d}`));
      lines.push('');
    }
  }

  lines.push('');
  lines.push('Bei Fragen oder Verschlechterung Ihrer Beschwerden kontaktieren Sie uns bitte.');

  return lines.join('\n');
}

// ── Helpers ──

function formatDateStr() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function categorizeDiagnoses(session) {
  const selected = session.selectedDiagnoses || [];
  const primary = [];
  const secondary = [];
  const other = [];
  (session.diagnoses || []).forEach((d) => {
    const idx = selected.indexOf(d.name);
    if (idx === 0) primary.push(d);
    else if (idx > 0) secondary.push(d);
    else other.push(d);
  });
  return { selected, primary, secondary, other };
}

// ── Template: Standard ──

function generateStandard(session) {
  const lines = [];
  lines.push('MEDIZINISCHER SESSIONBERICHT');
  lines.push('=============================');
  lines.push(`Session: ${session.name}`);
  lines.push(`Datum: ${formatDateStr()}`);
  lines.push('');

  if (session.summary && Array.isArray(session.summary) && session.summary.length > 0) {
    lines.push('ANAMNESE / ZUSAMMENFASSUNG');
    lines.push('---------------------------');
    session.summary.forEach((section) => {
      lines.push('');
      lines.push(`[${section.title}]`);
      (section.items || []).forEach((item) => {
        lines.push(`  \u2022 ${item}`);
      });
    });
    lines.push('');
  }

  if (session.manualNotes) {
    lines.push('ERGÄNZUNGEN DES ARZTES');
    lines.push('-----------------------');
    lines.push(session.manualNotes);
    lines.push('');
  }

  if (session.diagnoses && session.diagnoses.length > 0) {
    const { selected, primary, secondary, other } = categorizeDiagnoses(session);

    const formatDiagnosis = (d, num) => {
      lines.push('');
      lines.push(`${num}. ${d.name}`);
      if (d.reason) lines.push(`   Begründung: ${d.reason}`);
      if (d.diagnostics && d.diagnostics.length > 0) lines.push(`   Diagnostik: ${d.diagnostics.join(', ')}`);
      if (d.treatment && d.treatment.length > 0) lines.push(`   Behandlung: ${d.treatment.join(', ')}`);
      if (d.medications && d.medications.length > 0) lines.push(`   Medikamente: ${d.medications.join(', ')}`);
    };

    if (selected.length > 0) {
      let num = 1;
      if (primary.length > 0) {
        lines.push('HAUPTDIAGNOSE');
        lines.push('--------------');
        primary.forEach((d) => formatDiagnosis(d, num++));
        lines.push('');
      }
      if (secondary.length > 0) {
        lines.push('NEBENDIAGNOSEN');
        lines.push('---------------');
        secondary.forEach((d) => formatDiagnosis(d, num++));
        lines.push('');
      }
      if (other.length > 0) {
        lines.push('WEITERE DIFFERENTIALDIAGNOSEN');
        lines.push('-----------------------------');
        other.forEach((d) => formatDiagnosis(d, num++));
      }
    } else {
      lines.push('DIFFERENTIALDIAGNOSEN');
      lines.push('---------------------');
      session.diagnoses.forEach((d, i) => formatDiagnosis(d, i + 1));
    }
  }

  return lines.join('\n');
}

// ── Template: Kurzbericht ──

function generateKurzbericht(session) {
  const lines = [];
  lines.push('KURZBERICHT');
  lines.push('============');
  lines.push(`Session: ${session.name}  |  Datum: ${formatDateStr()}`);
  lines.push('');

  if (session.summary && Array.isArray(session.summary) && session.summary.length > 0) {
    lines.push('Symptome:');
    session.summary.forEach((section) => {
      (section.items || []).forEach((item) => {
        lines.push(`  - ${item}`);
      });
    });
    lines.push('');
  }

  if (session.manualNotes) {
    lines.push(`Ergänzungen: ${session.manualNotes}`);
    lines.push('');
  }

  if (session.diagnoses && session.diagnoses.length > 0) {
    const { selected, primary, secondary, other } = categorizeDiagnoses(session);

    if (selected.length > 0) {
      if (primary.length > 0) {
        lines.push(`Hauptdiagnose: ${primary.map((d) => d.name).join(', ')}`);
      }
      if (secondary.length > 0) {
        lines.push(`Nebendiagnosen: ${secondary.map((d) => d.name).join(', ')}`);
      }
      if (other.length > 0) {
        lines.push(`Weitere DD: ${other.map((d) => d.name).join(', ')}`);
      }
    } else {
      lines.push('Differentialdiagnosen:');
      session.diagnoses.forEach((d, i) => {
        lines.push(`  ${i + 1}. ${d.name}`);
      });
    }
  }

  return lines.join('\n');
}

// ── Template: Arztbrief ──

function generateArztbrief(session) {
  const lines = [];
  const dateStr = formatDateStr();

  lines.push(`Befundbericht vom ${dateStr}`);
  lines.push('');
  lines.push(`Betr.: ${session.name}`);
  lines.push('');
  lines.push('Sehr geehrte Kolleginnen und Kollegen,');
  lines.push('');
  lines.push('wir berichten über o.g. Patienten, der sich in unserer Sprechstunde vorstellte.');
  lines.push('');

  if (session.summary && Array.isArray(session.summary) && session.summary.length > 0) {
    lines.push('Anamnese:');
    session.summary.forEach((section) => {
      const items = section.items || [];
      if (items.length > 0) {
        lines.push(`${section.title}: ${items.join('. ')}.`);
      }
    });
    lines.push('');
  }

  if (session.manualNotes) {
    lines.push('Ergänzende Anmerkungen:');
    lines.push(session.manualNotes);
    lines.push('');
  }

  if (session.diagnoses && session.diagnoses.length > 0) {
    const { selected, primary, secondary, other } = categorizeDiagnoses(session);

    lines.push('Diagnosen:');
    if (selected.length > 0) {
      if (primary.length > 0) {
        primary.forEach((d) => {
          lines.push(`  Hauptdiagnose: ${d.name}`);
          if (d.reason) lines.push(`  Begründung: ${d.reason}`);
        });
      }
      if (secondary.length > 0) {
        secondary.forEach((d) => {
          lines.push(`  Nebendiagnose: ${d.name}`);
        });
      }
    } else {
      session.diagnoses.forEach((d, i) => {
        lines.push(`  ${i + 1}. ${d.name} – ${d.reason || ''}`);
      });
    }
    lines.push('');

    const allSelected = [...primary, ...secondary];
    const relevantDiagnoses = allSelected.length > 0 ? allSelected : session.diagnoses;

    const allMeds = relevantDiagnoses.flatMap((d) => d.medications || []);
    const allTreatments = relevantDiagnoses.flatMap((d) => d.treatment || []);
    const allDiagnostics = relevantDiagnoses.flatMap((d) => d.diagnostics || []);

    if (allDiagnostics.length > 0) {
      lines.push(`Empfohlene Diagnostik: ${[...new Set(allDiagnostics)].join(', ')}.`);
    }
    if (allTreatments.length > 0) {
      lines.push(`Therapieempfehlung: ${[...new Set(allTreatments)].join(', ')}.`);
    }
    if (allMeds.length > 0) {
      lines.push(`Medikation: ${[...new Set(allMeds)].join(', ')}.`);
    }
    lines.push('');
  }

  lines.push('Für Rückfragen stehen wir jederzeit zur Verfügung.');
  lines.push('');
  lines.push('Mit freundlichen kollegialen Grüßen');

  return lines.join('\n');
}

// ── Template: Stichpunkte ──

function generateStichpunkte(session) {
  const lines = [];
  lines.push(`${session.name} | ${formatDateStr()}`);
  lines.push('');

  if (session.summary && Array.isArray(session.summary) && session.summary.length > 0) {
    session.summary.forEach((section) => {
      lines.push(`# ${section.title}`);
      (section.items || []).forEach((item) => {
        lines.push(`- ${item}`);
      });
    });
    lines.push('');
  }

  if (session.manualNotes) {
    lines.push('# Ergänzungen');
    lines.push(`- ${session.manualNotes}`);
    lines.push('');
  }

  if (session.diagnoses && session.diagnoses.length > 0) {
    const { selected, primary, secondary, other } = categorizeDiagnoses(session);

    if (selected.length > 0) {
      if (primary.length > 0) {
        lines.push('# Hauptdiagnose');
        primary.forEach((d) => {
          lines.push(`- ${d.name}`);
          if (d.diagnostics && d.diagnostics.length > 0) lines.push(`  Dx: ${d.diagnostics.join(', ')}`);
          if (d.treatment && d.treatment.length > 0) lines.push(`  Tx: ${d.treatment.join(', ')}`);
          if (d.medications && d.medications.length > 0) lines.push(`  Rx: ${d.medications.join(', ')}`);
        });
      }
      if (secondary.length > 0) {
        lines.push('# Nebendiagnosen');
        secondary.forEach((d) => {
          lines.push(`- ${d.name}`);
          if (d.diagnostics && d.diagnostics.length > 0) lines.push(`  Dx: ${d.diagnostics.join(', ')}`);
          if (d.treatment && d.treatment.length > 0) lines.push(`  Tx: ${d.treatment.join(', ')}`);
          if (d.medications && d.medications.length > 0) lines.push(`  Rx: ${d.medications.join(', ')}`);
        });
      }
      if (other.length > 0) {
        lines.push('# Weitere DD');
        other.forEach((d) => {
          lines.push(`- ${d.name}`);
        });
      }
    } else {
      lines.push('# Differentialdiagnosen');
      session.diagnoses.forEach((d) => {
        lines.push(`- ${d.name}`);
        if (d.diagnostics && d.diagnostics.length > 0) lines.push(`  Dx: ${d.diagnostics.join(', ')}`);
        if (d.treatment && d.treatment.length > 0) lines.push(`  Tx: ${d.treatment.join(', ')}`);
        if (d.medications && d.medications.length > 0) lines.push(`  Rx: ${d.medications.join(', ')}`);
      });
    }
  }

  return lines.join('\n');
}

// ── Template-Registry ──

const TEMPLATES = {
  standard: generateStandard,
  kurzbericht: generateKurzbericht,
  arztbrief: generateArztbrief,
  stichpunkte: generateStichpunkte,
};

// ── Component ──

function SessionReportModal({ session, onClose, onConfirm }) {
  const { settings } = useSettings();
  const [activeTab, setActiveTab] = useState('doctor');
  const [copied, setCopied] = useState(false);
  const [patientReportSent, setPatientReportSent] = useState(false);

  const initialReport = useMemo(() => {
    const generator = TEMPLATES[settings.reportTemplate] || generateStandard;
    return generator(session);
  }, []);
  const [reportText, setReportText] = useState(initialReport);

  const initialPatientReport = useMemo(() => generatePatientReport(session), []);
  const [patientReportText, setPatientReportText] = useState(initialPatientReport);

  const exportPatientPDF = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    let y = 20;

    patientReportText.split('\n').forEach((line) => {
      if (line.startsWith('ZUSAMMENFASSUNG') || line.startsWith('===')) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
      } else if (line.startsWith('WAS WIR') || line.startsWith('IHRE ') || line.startsWith('EMPFOHLENE') || line.startsWith('GEPLANTE') || line.startsWith('MÖGLICHE') || line.startsWith('HINWEISE')) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
      } else if (line.startsWith('---') || line.startsWith('===')) {
        return;
      } else {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
      }
      const wrapped = doc.splitTextToSize(line, maxWidth);
      wrapped.forEach((wLine) => {
        if (y > 275) { doc.addPage(); y = 20; }
        doc.text(wLine, margin, y);
        y += 5.5;
      });
    });

    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
    const safeName = session.name.replace(/[^a-zA-Z0-9\-_ ]/g, '').replace(/\s+/g, '_');
    doc.save(`Patientenbericht_${safeName}_${dateStr}.pdf`);
  };

  const getFileBaseName = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
    const safeName = session.name.replace(/[^a-zA-Z0-9äöüÄÖÜß\-_ ]/g, '').replace(/\s+/g, '_');
    return `Bericht_${safeName}_${dateStr}`;
  };

  const exportPDF = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    let y = 20;

    const lines = reportText.split('\n');

    lines.forEach((line) => {
      if (line.startsWith('MEDIZINISCHER') || line.startsWith('ANAMNESE') || line.startsWith('ERGÄNZUNGEN') || line.startsWith('DIFFERENTIALDIAGNOSEN') || line.startsWith('HAUPTDIAGNOSE') || line.startsWith('NEBENDIAGNOSEN') || line.startsWith('WEITERE DIFFERENTIALDIAGNOSEN') || line.startsWith('KURZBERICHT') || line.startsWith('Befundbericht')) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
      } else if (line.startsWith('===') || line.startsWith('---')) {
        return;
      } else if ((line.startsWith('[') && line.endsWith(']')) || line.startsWith('#') || line.startsWith('Sehr geehrte') || line.startsWith('Mit freundlichen')) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
      } else {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
      }

      const wrapped = doc.splitTextToSize(line, maxWidth);
      wrapped.forEach((wLine) => {
        if (y > 275) {
          doc.addPage();
          y = 20;
        }
        doc.text(wLine, margin, y);
        y += 5.5;
      });
    });

    doc.save(`${getFileBaseName()}.pdf`);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = reportText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const exportWord = async () => {
    const lines = reportText.split('\n');
    const children = [];

    lines.forEach((line) => {
      if (line.startsWith('MEDIZINISCHER') || line.startsWith('KURZBERICHT') || line.startsWith('Befundbericht')) {
        children.push(new Paragraph({
          children: [new TextRun({ text: line, bold: true, size: 28, font: 'Calibri' })],
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.LEFT,
          spacing: { after: 100 },
        }));
      } else if (line.startsWith('===') || line.startsWith('---')) {
        // skip separators
      } else if (line.startsWith('ANAMNESE') || line.startsWith('ERGÄNZUNGEN') || line.startsWith('DIFFERENTIALDIAGNOSEN') || line.startsWith('HAUPTDIAGNOSE') || line.startsWith('NEBENDIAGNOSEN') || line.startsWith('WEITERE DIFFERENTIALDIAGNOSEN')) {
        children.push(new Paragraph({
          children: [new TextRun({ text: line, bold: true, size: 24, font: 'Calibri' })],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 100 },
        }));
      } else if ((line.startsWith('[') && line.endsWith(']'))) {
        children.push(new Paragraph({
          children: [new TextRun({ text: line.slice(1, -1), bold: true, size: 22, font: 'Calibri' })],
          spacing: { before: 200, after: 50 },
        }));
      } else if (line.startsWith('#')) {
        children.push(new Paragraph({
          children: [new TextRun({ text: line.replace(/^#+\s*/, ''), bold: true, size: 22, font: 'Calibri' })],
          spacing: { before: 200, after: 50 },
        }));
      } else if (line.trim().startsWith('\u2022') || line.trim().startsWith('-')) {
        children.push(new Paragraph({
          children: [new TextRun({ text: line.trim(), size: 22, font: 'Calibri' })],
          spacing: { after: 30 },
          indent: { left: 400 },
        }));
      } else if (line.startsWith('Sehr geehrte') || line.startsWith('Mit freundlichen')) {
        children.push(new Paragraph({
          children: [new TextRun({ text: line, bold: true, italics: true, size: 22, font: 'Calibri' })],
          spacing: { before: 200, after: 100 },
        }));
      } else if (line.trim() === '') {
        children.push(new Paragraph({ children: [], spacing: { after: 100 } }));
      } else {
        children.push(new Paragraph({
          children: [new TextRun({ text: line, size: 22, font: 'Calibri' })],
          spacing: { after: 50 },
        }));
      }
    });

    const doc = new Document({
      sections: [{ children }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${getFileBaseName()}.docx`);
  };

  return (
    <div className="session-modal-overlay" onClick={onClose}>
      <div className="report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="report-header">
          <h2>Abschlussbericht</h2>
          <button className="report-close-btn" onClick={onClose} title="Schließen">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="report-tabs">
          <button
            className={`report-tab${activeTab === 'doctor' ? ' active' : ''}`}
            onClick={() => setActiveTab('doctor')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Ärztliche Dokumentation
          </button>
          <button
            className={`report-tab${activeTab === 'patient' ? ' active' : ''}`}
            onClick={() => setActiveTab('patient')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
            Patientenbericht
            {patientReportSent && (
              <span className="report-tab-badge">Freigegeben</span>
            )}
          </button>
        </div>

        {activeTab === 'doctor' ? (
          <>
            <p className="report-hint">
              Der Bericht kann vor dem Exportieren bearbeitet werden.
            </p>
            <textarea
              className="report-textarea"
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              spellCheck={false}
            />
            <div className="report-actions">
              <div className="report-actions-left">
                <button className="report-btn report-btn-pdf" onClick={exportPDF}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" /><line x1="12" y1="18" x2="12" y2="12" /><polyline points="9 15 12 18 15 15" />
                  </svg>
                  PDF
                </button>
                <button className="report-btn report-btn-word" onClick={exportWord}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  Word
                </button>
                <button className="report-btn report-btn-copy" onClick={copyToClipboard}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  {copied ? 'Kopiert!' : 'Kopieren'}
                </button>
              </div>
              <button className="report-btn report-btn-save" onClick={onConfirm}>
                Speichern & Schließen
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="report-hint">
              Patientenfreundliche Zusammenfassung des Arztgesprächs. Bitte prüfen und bei Bedarf anpassen — dann für den Patienten freigeben.
            </p>
            <textarea
              className="report-textarea"
              value={patientReportText}
              onChange={(e) => setPatientReportText(e.target.value)}
              spellCheck={false}
            />
            <div className="report-actions">
              <div className="report-actions-left">
                <button className="report-btn report-btn-pdf" onClick={exportPatientPDF}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" /><line x1="12" y1="18" x2="12" y2="12" /><polyline points="9 15 12 18 15 15" />
                  </svg>
                  PDF für Patient
                </button>
              </div>
              {patientReportSent ? (
                <div className="report-send-success">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Bericht freigegeben — Patient wird benachrichtigt
                </div>
              ) : (
                <button className="report-btn report-btn-send" onClick={() => setPatientReportSent(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  Freigeben & an Patient senden
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SessionReportModal;
